// ✅ src/pages/VehicleV2/PeriodicEntryReport.jsx
// UPDATED — Production listing template
// 3-column filters • overflow locked • important list columns

import React, {
  useEffect,
  useMemo,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaArrowLeft,
  FaEye,
  FaSyncAlt,
  FaChevronDown,
  FaChevronUp,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_BASE = "https://nissi-india.com/api/periodicEntryReport";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const fmtNum = (v) => {
  if (v === null || v === undefined || v === "") return "";
  const n = Number(String(v).replace(/,/g, ""));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const createDefaultFilters = (branchType, branchId) => ({
  fromDate: "",
  toDate: "",
  branch: branchType === "HO" ? "Select" : String(branchId),
  store: "Select",
  status: "Select",
  vehicleStatus: "Select",
  vehicleNo: "",
  vehicleCode: "",
  vehicleMainType: "Select",
});

export default function PeriodicEntryReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const tableHeaderClass = buttonClass;

  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";
  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";
  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = toInt(branchIdRaw, 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchtype: toStr(branchType || "HO"),
      branchid: sessionBranchId,
      userid: toInt(loginUserId, 0),
    }),
    [branchType, sessionBranchId, loginUserId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchtype, session.branchid),
    [session.branchtype, session.branchid]
  );

  const didInitRef = useRef(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);
  const isBusy = pageLoading || dropdownLoading || listLoading;
  const [showFilters, setShowFilters] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  const [ddl, setDdl] = useState({ branches: [], stores: [], vehicleMainTypes: [] });
  const [filters, setFilters] = useState(defaultFilters);
  const [rows, setRows] = useState([]);

  const totalRows = rows.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(totalRows / itemsPerPage)), [totalRows]);
  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = useMemo(() => rows.slice(indexOfFirst, indexOfLast), [rows, indexOfFirst, indexOfLast]);

  const fieldClass = useMemo(
    () =>
      `w-full max-w-full min-w-0 box-border px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
        isDark
          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () => `block mb-2 text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`,
    [isDark]
  );

  const tdClass = useMemo(
    () => `px-4 py-3 text-sm ${isDark ? "text-gray-300" : "text-gray-900"}`,
    [isDark]
  );

  const getAlertClass = useCallback((alertText) => {
    const a = toStr(alertText).toLowerCase();
    if (a.includes("expired")) return isDark ? "bg-red-900 text-red-100" : "bg-red-100 text-red-800";
    if (a.includes("nearly")) return isDark ? "bg-yellow-900 text-yellow-100" : "bg-yellow-100 text-yellow-800";
    return isDark ? "bg-green-900 text-green-100" : "bg-green-100 text-green-800";
  }, [isDark]);

  const setField = useCallback((name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const loadInit = useCallback(async () => {
    try {
      setDropdownLoading(true);
      const res = await axios.get(`${API_BASE}/init`, {
        params: { branchId: session.branchid || 0 },
        headers: commonHeaders,
      });
      const payload = res.data || {};
      if (!payload.success) throw new Error(payload.message || "Init failed");
      setDdl({
        branches: toList(payload.branches),
        stores: toList(payload.stores),
        vehicleMainTypes: toList(payload.vehicleMainTypes),
      });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load dropdowns");
      setDdl({ branches: [], stores: [], vehicleMainTypes: [] });
    } finally {
      setDropdownLoading(false);
    }
  }, [session.branchid, commonHeaders]);

  const loadList = useCallback(async (nextFilters = filters) => {
    try {
      setListLoading(true);
      const params = {
        branchtype: session.branchtype,
        branchid: session.branchid,
        userid: session.userid,
        fromDate: nextFilters.fromDate || "",
        toDate: nextFilters.toDate || "",
        branch: session.branchtype === "HO" ? nextFilters.branch || "Select" : String(session.branchid),
        store: nextFilters.store || "Select",
        status: nextFilters.status || "Select",
        vehicleStatus: nextFilters.vehicleStatus || "Select",
        vehicleNo: nextFilters.vehicleNo || "",
        vehicleCode: nextFilters.vehicleCode || "",
        vehicleMainType: nextFilters.vehicleMainType || "Select",
      };
      const res = await axios.get(`${API_BASE}/list`, { params, headers: commonHeaders });
      const payload = res.data || {};
      if (!payload.success) throw new Error(payload.message || "List failed");
      setRows(toList(payload.rows));
      setCurrentPage(1);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err.message || "Failed to load report");
      setRows([]);
      setCurrentPage(1);
    } finally {
      setListLoading(false);
    }
  }, [filters, session, commonHeaders]);

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);
      setFilters(defaultFilters);
      setRows([]);
      setCurrentPage(1);
      await loadInit();
    } finally {
      setPageLoading(false);
    }
  }, [defaultFilters, loadInit]);

  const handleChange = useCallback((e) => {
    setField(e.target.name, e.target.value);
  }, [setField]);

  const handleSearch = useCallback(async () => {
    await loadList(filters);
    toast.success("Report loaded");
  }, [loadList, filters]);

  const handleViewAll = useCallback(async () => {
    const reset = createDefaultFilters(session.branchtype, session.branchid);
    setFilters(reset);
    await loadList(reset);
    toast.info("Showing all records");
  }, [session.branchtype, session.branchid, loadList]);

  const handleClearFilters = useCallback(() => {
    const reset = createDefaultFilters(session.branchtype, session.branchid);
    setFilters(reset);
    setRows([]);
    setCurrentPage(1);
    toast.info("Filters reset. Click Search to load.");
  }, [session.branchtype, session.branchid]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    await loadList(defaultFilters);
    toast.info("Page refreshed");
  }, [loadPage, loadList, defaultFilters]);

  const handlePrintList = useCallback(() => {
    try {
      localStorage.setItem(
        "PERIODIC_ENTRY_PRINT_LIST",
        JSON.stringify({ session, filters, rows, printedAt: new Date().toISOString() })
      );
      window.open("/vehicle/PeriodicENtryReport_AllPrint", "_blank", "width=1100,height=800,scrollbars=yes");
      toast.info("Printing all records");
    } catch (e) {
      console.error(e);
      toast.error("Print failed");
    }
  }, [session, filters, rows]);

  const handlePrintRow = useCallback((r) => {
    const vehicleid = r?.fld_vehicleid || 0;
    const maintenanceid = r?.fld_maintenanceid || 0;
    window.open(
      `/vehicle/PeriodicEntryReport_Print?vehicleid=${vehicleid}&maintenanceid=${maintenanceid}`,
      "_blank",
      "width=1100,height=800,scrollbars=yes"
    );
  }, []);

  const handleBack = useCallback(() => navigate(-1), [navigate]);

  const paginate = useCallback((p) => {
    setCurrentPage(Math.min(Math.max(1, p), totalPages));
  }, [totalPages]);

  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    (async () => {
      await loadPage();
      await loadList(defaultFilters);
    })();
  }, [loadPage, loadList, defaultFilters]);

  const statusText = pageLoading
    ? "Loading page..."
    : dropdownLoading
    ? "Loading dropdowns..."
    : listLoading
    ? "Searching..."
    : "Ready";

  return (
    <VehicleLayoutWrapper>
      <div className={`w-full max-w-full min-w-0 overflow-x-hidden box-border min-h-screen p-4 md:p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-2xl md:text-3xl">PERIODIC ENTRY REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={`w-full max-w-full min-w-0 overflow-hidden p-4 md:p-6 mt-6 rounded-2xl shadow-xl border box-border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : "bg-white text-black"
        }`}>
          <div className="flex justify-between items-center mb-6 flex-wrap gap-3">
            <div className="min-w-0">
              <h2 className={`text-xl md:text-2xl font-bold break-words ${isDark ? "text-white" : "text-gray-900"}`}>
                Periodic Entry Report
              </h2>
              <p className={`text-sm mt-1 ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                Branch ID: {session.branchid || "-"} • User ID: {session.userid || "-"}
              </p>
            </div>
            <div className="flex items-center gap-2 md:gap-3 flex-wrap">
              <button onClick={handlePrintList} disabled={isBusy || rows.length === 0} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 ${buttonClass} ${hoverClass} text-white ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaPrint /> Print
              </button>
              <button onClick={handleRefresh} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaSyncAlt /> Refresh
              </button>
              <button onClick={handleBack} disabled={isBusy} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>
                <FaArrowLeft /> Back
              </button>
              <button onClick={() => setShowFilters((p) => !p)} type="button" className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform transition duration-200 ${isDark ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600" : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"}`}>
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>
            </div>
          </div>

          {showFilters && (
            <div className={`p-4 md:p-6 rounded-lg mb-6 overflow-hidden ${isDark ? "bg-gray-800/50" : "bg-blue-50"}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <h3 className={`text-lg font-semibold ${isDark ? "text-white" : "text-blue-900"}`}>Search Criteria</h3>
                <div className="flex gap-2 flex-wrap">
                  <button onClick={handleClearFilters} type="button" disabled={isBusy} className={`px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}>Clear</button>
                  <button onClick={handleViewAll} type="button" disabled={isBusy} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold border ${isDark ? "border-gray-600 text-gray-200 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-white"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}><FaEye /> View All</button>
                  <button onClick={handleSearch} type="button" disabled={isBusy} className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${isBusy ? "opacity-60 cursor-not-allowed" : ""} ${buttonClass} ${hoverClass} text-white`}><FaSearch /> {listLoading ? "Searching..." : "Search"}</button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
                <div className="min-w-0">
                  <label className={labelClass}>From Date</label>
                  <input type="date" name="fromDate" value={filters.fromDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>To Date</label>
                  <input type="date" name="toDate" value={filters.toDate} onChange={handleChange} className={fieldClass} disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Branch</label>
                  <select name="branch" value={filters.branch} onChange={handleChange} className={fieldClass} disabled={isBusy || session.branchtype !== "HO"}>
                    <option value="Select">Select</option>
                    {ddl.branches.map((b, i) => (
                      <option key={`${b.fld_branchid || i}-${i}`} value={String(b.fld_branchid || "Select")}>
                        {b.fld_BranchName || b.branchName || "Select"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Store</label>
                  <select name="store" value={filters.store} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {ddl.stores.map((s, i) => (
                      <option key={`${s.fld_storeid || i}-${i}`} value={String(s.fld_storeid || "Select")}>
                        {s.fld_storename || s.storeName || "Select"}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Status</label>
                  <select name="status" value={filters.status} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="ServiceNearly">Service Nearly</option>
                    <option value="ServiceExpired">Service Expired</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Status</label>
                  <select name="vehicleStatus" value={filters.vehicleStatus} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    <option value="All">All</option>
                    <option value="Running">Running</option>
                    <option value="Stopped">Stopped</option>
                  </select>
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Number</label>
                  <input type="text" name="vehicleNo" value={filters.vehicleNo} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle number" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Code</label>
                  <input type="text" name="vehicleCode" value={filters.vehicleCode} onChange={handleChange} className={fieldClass} placeholder="Enter vehicle code" disabled={isBusy} />
                </div>
                <div className="min-w-0">
                  <label className={labelClass}>Vehicle Main Type</label>
                  <select name="vehicleMainType" value={filters.vehicleMainType} onChange={handleChange} className={fieldClass} disabled={isBusy}>
                    <option value="Select">Select</option>
                    {ddl.vehicleMainTypes.map((v, i) => {
                      const label = v.fld_vehicletypename || v.fld_vehicle_subtypename || v.VehicleType || v.name || Object.values(v || {})[0] || "Type";
                      const value = v.fld_vehicletypeindex || v.fld_vehicletypename || v.fld_vehicle_subtypename || label;
                      return <option key={`${value}-${i}`} value={String(value)}>{String(label)}</option>;
                    })}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"}`}>Total Records: {totalRows}</div>
            <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>{statusText}</div>
          </div>

          <div className="w-full max-w-full overflow-x-auto rounded-lg border shadow-lg">
            {pageLoading || dropdownLoading || listLoading ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-300" : "text-gray-600"}`}>Loading...</div>
              </div>
            ) : currentItems.length === 0 ? (
              <div className={`p-16 text-center ${isDark ? "bg-gray-800" : "bg-gray-50"}`}>
                <div className={`text-lg font-medium ${isDark ? "text-gray-400" : "text-gray-500"}`}>No Records Found</div>
                <p className={`mt-2 text-sm ${isDark ? "text-gray-500" : "text-gray-400"}`}>Try adjusting your search criteria or click View All.</p>
              </div>
            ) : (
              <table className={`w-full ${isDark ? "bg-gray-800" : "bg-white"}`}>
                <thead className={`${tableHeaderClass} text-white`}>
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">S.No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Name</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vehicle Reg No</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">SubAssembly</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Next Service</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Remaining</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Alert</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Print</th>
                  </tr>
                </thead>
                <tbody className={`${isDark ? "divide-gray-700" : "divide-gray-200"} divide-y`}>
                  {currentItems.map((r, idx) => {
                    const vName = r.fld_vehicle_name || r.name || "";
                    const regNo = r.fld_vehicle_regno || r.regNo || "";
                    const subAsm = r.fld_vehicle_subassemblyname || r.subAssembly || "";
                    const nextService = r.fld_usagelimit_calc ?? r.nextService ?? "";
                    const remaining = r.fld_Remaininghr ?? r.remaining ?? "";
                    const alertText = r.alertText || r.alert || "Normal";
                    return (
                      <tr key={r.fld_vehicleid ? `${r.fld_vehicleid}-${r.fld_maintenanceid}-${idx}` : idx} className={`${idx % 2 === 0 ? (isDark ? "bg-gray-800" : "bg-white") : (isDark ? "bg-gray-750" : "bg-gray-50")} transition-colors ${isDark ? "hover:bg-gray-700" : "hover:bg-blue-50"}`}>
                        <td className={tdClass}>{indexOfFirst + idx + 1}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-white" : "text-gray-900"}`}>{vName || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? "text-blue-400" : "text-blue-600"}`}>{regNo || "-"}</td>
                        <td className={tdClass}>{subAsm || "-"}</td>
                        <td className={tdClass}>{fmtNum(nextService) || "-"}</td>
                        <td className={`px-4 py-3 text-sm font-semibold ${Number(remaining) > 0 ? (isDark ? "text-green-400" : "text-green-600") : (isDark ? "text-red-400" : "text-red-600")}`}>
                          {Number(remaining) > 0 ? `+${fmtNum(remaining)}` : fmtNum(remaining) || "-"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getAlertClass(alertText)}`}>{alertText}</span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button onClick={() => handlePrintRow(r)} disabled={isBusy} className={`p-2 rounded transition ${isDark ? "text-white hover:bg-gray-600" : "text-gray-800 hover:bg-gray-200"} ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`} title="Print" type="button">
                            <FaPrint className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {rows.length > 0 && (
            <div className="mt-6 flex flex-col sm:flex-row justify-between items-center gap-3">
              <div className={`text-sm ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                Showing {indexOfFirst + 1} to {Math.min(indexOfLast, rows.length)} of {rows.length} entries
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button onClick={() => paginate(currentPage - 1)} disabled={currentPage === 1 || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === 1 || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Prev</button>
                <div className={`px-4 py-2 border rounded-lg ${isDark ? "border-gray-600 text-gray-300" : "border-gray-300 text-gray-700"}`}>Page {currentPage} / {totalPages}</div>
                <button onClick={() => paginate(currentPage + 1)} disabled={currentPage === totalPages || isBusy} type="button" className={`px-4 py-2 border rounded-lg font-semibold ${isDark ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 text-gray-700 hover:bg-gray-100"} ${currentPage === totalPages || isBusy ? "opacity-50 cursor-not-allowed" : ""}`}>Next</button>
              </div>
            </div>
          )}

          <ToastContainer position="top-right" autoClose={2500} closeOnClick pauseOnHover theme={isDark ? "dark" : "light"} />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}